import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCountries } from "@/hooks/useCountries";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { RiskBadge } from "./RiskBadge";
import { formatFileSize } from "@/shared/core";
import type { DocumentReviewsTableProps } from "../types/component.types";

export const DocumentReviewsTable: React.FC<DocumentReviewsTableProps> = ({
  reviews,
  basePath = "",
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getCountryName } = useCountries();
  const locale = i18n.language === "es" ? es : enUS;

  const handleRowClick = (reviewId: string) => {
    navigate(`${basePath}/document-review/${reviewId}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-atlantis-600" />;
      case "failed":
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case "processing":
        return (
          <div className="h-5 w-5 border-2 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        );
      default:
        return <ClockIcon className="h-5 w-5 text-titan" />;
    }
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-titan">{t("documentReview.empty")}</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile card view */}
      <div className="lg:hidden divide-y divide-tiber-200">
        {reviews.map((review) => (
          <div
            key={review.id}
            onClick={() => handleRowClick(review.id)}
            className="p-4 flex flex-col gap-2 hover:bg-romance-50 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-juridico truncate">
                  {review.document_name}
                </p>
                <p className="text-xs text-titan">{review.document_type}</p>
              </div>
              <div className="shrink-0">
                {review.analysis_status === "completed" &&
                review.analysis_result?.overall_risk ? (
                  <RiskBadge risk={review.analysis_result.overall_risk} size="sm" />
                ) : (
                  getStatusIcon(review.analysis_status)
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-titan flex-wrap">
              <span>{getCountryName(review.country)}</span>
              {review.file_size && <span>{formatFileSize(review.file_size)}</span>}
              <span>
                {formatDistanceToNow(new Date(review.created_at), {
                  addSuffix: true,
                  locale,
                })}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table view */}
      <div className="hidden lg:block overflow-x-auto min-w-0">
      <table className="min-w-full divide-y divide-tiber-200">
        <thead className="bg-romance-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.document")}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.type")}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.country")}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.status")}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.risk")}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider"
            >
              {t("documentReview.table.date")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-tiber-200">
          {reviews.map((review) => (
            <tr
              key={review.id}
              onClick={() => handleRowClick(review.id)}
              className="hover:bg-romance-50 cursor-pointer transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-col">
                  <div className="text-sm font-medium text-juridico truncate max-w-xs">
                    {review.document_name}
                  </div>
                  {review.file_size && (
                    <div className="text-xs text-titan">
                      {formatFileSize(review.file_size)}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-juridico">
                  {review.document_type}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-juridico">
                  {getCountryName(review.country)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {getStatusIcon(review.analysis_status)}
                  <span className="text-sm text-juridico">
                    {t(`documentReview.status.${review.analysis_status}`)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {review.analysis_status === "completed" &&
                review.analysis_result?.overall_risk ? (
                  <RiskBadge
                    risk={review.analysis_result.overall_risk}
                    size="sm"
                  />
                ) : (
                  <span className="text-sm text-titan">-</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-titan">
                {formatDistanceToNow(new Date(review.created_at), {
                  addSuffix: true,
                  locale,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
};
